import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Per-feature blurred previews for LockedFeature.js. Each mock is a static,
 * non-interactive recreation of the REAL panel it gates — same palette,
 * same shapes, same layout language as the actual component (AnalyticsPanel,
 * TeamsPanel, HubPanel, CampaignsPanel, FollowUpsPanel, TeamAnalyticsPanel,
 * BrandKitEditor) — instead of one generic "populated dashboard" placeholder
 * reused for every feature. Never real data (children never mount while
 * locked); every value here is a fixed, representative example.
 *
 * Only features actually wrapped in <LockedFeature> get a dedicated mock;
 * anything else falls back to GenericPreviewMock in LockedFeature.js.
 */

const wrap = { filter: 'blur(6px) saturate(1.3)', pointerEvents: 'none', userSelect: 'none', padding: 24 };
const FONT = "'Inter',system-ui,sans-serif";

// ── Analytics ────────────────────────────────────────────────────────────
// Mirrors AnalyticsPanel's 4 monochrome METRICS cards + a trend chart area.
const AnalyticsMock = () => {
  const { t } = useLanguage();
  const metrics = [
    { label: t('lockedFeatureMock.analytics.totalVisits', 'Visitas Totales'), value: '1,284', icon: '◎' },
    { label: t('lockedFeatureMock.analytics.socialClicks', 'Clics en Redes'), value: '312', icon: '↗' },
    { label: t('lockedFeatureMock.analytics.savedContacts', 'Contactos Guardados'), value: '96', icon: '☎' },
    { label: t('lockedFeatureMock.analytics.engagementRate', 'Tasa de Interacción'), value: '24%', icon: '△' },
  ];
  const bars = [38, 52, 44, 61, 58, 72, 66, 80, 74, 90];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        {metrics.map((m, i) => (
          <div key={i} style={{ flex: 1, minWidth: 100, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, background: 'rgba(10,10,10,0.06)', color: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, marginBottom: 10 }}>{m.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em', fontFamily: FONT }}>{m.value}</div>
            <div style={{ fontSize: 10.5, color: '#9A9A9A', marginTop: 3 }}>{m.label}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, padding: '16px 16px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9A9A9A', marginBottom: 12 }}>{t('lockedFeatureMock.analytics.chartTitle', 'Visitas · últimos 14 días')}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 70 }}>
          {bars.map((h, i) => (
            <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0', background: i === bars.length - 1 ? '#0A0A0A' : 'rgba(10,10,10,0.15)' }} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Teams ────────────────────────────────────────────────────────────────
// Monochrome per TeamsPanel's own design exception (no green/orange).
const TeamsMock = () => {
  const { t } = useLanguage();
  const members = [
    { name: 'Camila Rojas', role: t('lockedFeatureMock.teams.sales', 'Ventas'), active: true },
    { name: 'Diego Fuentes', role: t('lockedFeatureMock.teams.marketing', 'Marketing'), active: true },
    { name: 'Valentina Soto', role: t('lockedFeatureMock.teams.sales', 'Ventas'), active: true },
    { name: 'Ignacio Muñoz', role: t('lockedFeatureMock.teams.support', 'Soporte'), active: false },
  ];
  const stats = [
    ['24', t('lockedFeatureMock.teams.members', 'Miembros')],
    ['18', t('lockedFeatureMock.teams.activeStat', 'Activos')],
    ['6', t('lockedFeatureMock.teams.profiles', 'Perfiles')],
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {stats.map(([v, l], i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '10px 14px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0A0A0A', fontFamily: FONT }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, overflow: 'hidden' }}>
        {members.map((m, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < members.length - 1 ? '1px solid #F0F0F0' : 'none' }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg,#525252cc,#0A0A0Acc)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13,
            }}>{m.name[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0A0A0A' }}>{m.name}</div>
              <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 1 }}>{m.role}</div>
            </div>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
              background: m.active ? 'rgba(10,10,10,0.08)' : '#F0F0F0',
              color: m.active ? '#0A0A0A' : '#9A9A9A',
            }}>{m.active ? t('lockedFeatureMock.active', 'Activo') : t('lockedFeatureMock.inactive', 'Inactivo')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Team Analytics ───────────────────────────────────────────────────────
// Ranking rows: avatar, name, a visits bar, clicks/leads columns.
const TeamAnalyticsMock = () => {
  const { t } = useLanguage();
  const rows = [
    { name: 'Camila Rojas', visits: 92, pct: 100, clicks: 31, leads: 12 },
    { name: 'Diego Fuentes', visits: 71, pct: 77, clicks: 22, leads: 9 },
    { name: 'Valentina Soto', visits: 54, pct: 59, clicks: 18, leads: 6 },
    { name: 'Ignacio Muñoz', visits: 33, pct: 36, clicks: 10, leads: 3 },
  ];
  const stats = [
    ['4', t('lockedFeatureMock.teams.profiles', 'Perfiles')],
    ['250', t('lockedFeatureMock.teamAnalytics.visits', 'Visitas')],
    ['81', t('lockedFeatureMock.teamAnalytics.clicks', 'Clics')],
    ['30', 'Leads'],
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {stats.map(([v, l], i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0A0A0A', fontFamily: FONT }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, padding: '6px 16px' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < rows.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
            <span style={{ fontSize: 11, color: '#9A9A9A', width: 14 }}>{i + 1}</span>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg,#0A0A0A,#525252)', color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{r.name[0]}</div>
            <div style={{ flex: '0 0 110px', fontSize: 12, fontWeight: 500, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
            <div style={{ flex: 1, height: 5, borderRadius: 99, background: '#F0F0F0', overflow: 'hidden' }}>
              <div style={{ width: `${r.pct}%`, height: '100%', background: '#0A0A0A', borderRadius: 99 }} />
            </div>
            <span style={{ width: 44, textAlign: 'right', fontSize: 11.5, color: '#6B6B6B' }}>{r.clicks}</span>
            <span style={{ width: 34, textAlign: 'right', fontSize: 11.5, color: '#6B6B6B' }}>{r.leads}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Hub ──────────────────────────────────────────────────────────────────
// Rows with the real STATUS_STYLE colors from HubPanel (green/orange/dark/red).
const HubMock = () => {
  const { t } = useLanguage();
  const rows = [
    { name: 'Martín Pérez', status: t('hub.status.new', 'Nuevo'), bg: 'rgba(22,163,74,0.12)', color: '#16A34A' },
    { name: 'Sofía Herrera', status: t('hub.status.meeting_scheduled', 'Reunión agendada'), bg: 'rgba(217,119,6,0.14)', color: '#D97706' },
    { name: 'Cristóbal Vega', status: t('hub.status.qualified', 'Calificado'), bg: 'rgba(10,10,10,0.08)', color: '#0A0A0A' },
    { name: 'Antonia Reyes', status: t('hub.status.won', 'Ganado'), bg: 'rgba(22,163,74,0.16)', color: '#16A34A' },
  ];
  const stats = [
    ['58', t('lockedFeatureMock.hub.contacts', 'Contactos')],
    ['12', t('lockedFeatureMock.hub.newStat', 'Nuevos')],
    ['9', t('lockedFeatureMock.hub.meetings', 'Reuniones')],
    ['5', t('lockedFeatureMock.hub.wonStat', 'Ganados')],
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {stats.map(([v, l], i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0A0A0A', fontFamily: FONT }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, overflow: 'hidden' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < rows.length - 1 ? '1px solid #F0F0F0' : 'none' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#F0F0F0', flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12.5, fontWeight: 500, color: '#0A0A0A' }}>{r.name}</div>
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: r.bg, color: r.color, whiteSpace: 'nowrap' }}>{r.status}</span>
            <div style={{ display: 'flex', gap: 5 }}>
              <div style={{ width: 22, height: 22, borderRadius: 7, border: '1px solid #E5E5E5' }} />
              <div style={{ width: 22, height: 22, borderRadius: 7, border: '1px solid #E5E5E5' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Campaigns ────────────────────────────────────────────────────────────
// Funnel breakdown bars, same shape/labels as CampaignsPanel's real funnel.
const CampaignsMock = () => {
  const { t } = useLanguage();
  const funnel = [
    { label: t('hub.status.new', 'Nuevo'), count: 42, pct: 100 },
    { label: t('hub.status.contacted', 'Contactado'), count: 27, pct: 64 },
    { label: t('hub.status.qualified', 'Calificado'), count: 14, pct: 33 },
    { label: t('hub.status.won', 'Ganado'), count: 6, pct: 14 },
  ];
  const stats = [
    ['3', t('lockedFeatureMock.campaigns.activeEvents', 'Eventos activos')],
    ['42', 'Leads'],
    ['14%', t('lockedFeatureMock.campaigns.conversion', 'Conversión')],
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {stats.map(([v, l], i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0A0A0A', fontFamily: FONT }}>{v}</div>
            <div style={{ fontSize: 10, color: '#9A9A9A', marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, padding: '16px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9A9A9A', marginBottom: 12 }}>Feria Comercial 2026</div>
        {funnel.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < funnel.length - 1 ? 10 : 0 }}>
            <span style={{ width: 78, flexShrink: 0, fontSize: 12, fontWeight: 500, color: '#0A0A0A' }}>{f.label}</span>
            <div style={{ flex: 1, height: 8, borderRadius: 99, background: '#F0F0F0', overflow: 'hidden' }}>
              <div style={{ width: `${f.pct}%`, height: '100%', borderRadius: 99, background: '#0A0A0A' }} />
            </div>
            <span style={{ width: 26, textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#0A0A0A' }}>{f.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Follow-ups (reminders) ───────────────────────────────────────────────
// 3-step sequence timeline: Conexión → Seguimiento → Aporte de valor.
const RemindersMock = () => {
  const { t } = useLanguage();
  const steps = [
    { num: 'Email 1', name: t('followups.step.connection.name', 'Conexión'), when: t('followups.delay.0', 'Inmediatamente'), on: true },
    { num: 'Email 2', name: t('followups.step.followup.name', 'Seguimiento'), when: t('lockedFeatureMock.reminders.day3', 'Día 3'), on: true },
    { num: 'Email 3', name: t('followups.step.value.name', 'Aporte de valor'), when: t('lockedFeatureMock.reminders.day6', 'Día 6'), on: false },
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 16, padding: '18px 16px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A', marginBottom: 14 }}>{t('followups.timeline.title', 'Tu secuencia')}</div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ flex: 1, padding: '10px 12px', borderRadius: 12, background: i === 0 ? '#F5F5F7' : 'transparent' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#B0B0B0', letterSpacing: '.04em', marginBottom: 5 }}>{s.num}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0A0A0A', marginBottom: 3 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 7 }}>{s.when}</div>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                  background: s.on ? 'rgba(22,163,74,.1)' : '#F0F0F0',
                  color: s.on ? '#16A34A' : '#9A9A9A',
                }}>{s.on ? t('lockedFeatureMock.active', 'Activo') : t('lockedFeatureMock.inactive', 'Inactivo')}</span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ width: 30, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#DCDCDC', fontSize: 16 }}>›</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 14, background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 14, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9A9A9A', marginBottom: 8 }}>{t('followups.recent.title', 'Actividad reciente')}</div>
        {['Sofía Herrera', 'Martín Pérez'].map((n, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: i > 0 ? '1px solid #F0F0F0' : 'none' }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#0A0A0A' }}>{n}</span>
            <span style={{ fontSize: 11, color: '#9A9A9A' }}>{t('followups.seqStatus.active', 'En seguimiento')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Brand Kit ────────────────────────────────────────────────────────────
// Theme swatch cards, mirroring COLOR_THEMES (constants/themes.js).
const BrandKitMock = () => {
  const { t } = useLanguage();
  const themes = [
    { key: 'blue', name: t('theme.blue', 'Azul Profesional'), button: '#3b82f6', accent: '#dbeafe' },
    { key: 'green', name: t('theme.green', 'Verde Naturaleza'), button: '#10b981', accent: '#d1fae5' },
    { key: 'purple', name: t('theme.purple', 'Púrpura Moderno'), button: '#8b5cf6', accent: '#ede9fe' },
  ];
  return (
    <div aria-hidden="true" style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '12px 14px' }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#F5F5F7', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0A0A0A' }}>{t('lockedFeatureMock.brandKit.companyLogo', 'Logo de la empresa')}</div>
          <div style={{ fontSize: 10.5, color: '#9A9A9A', marginTop: 2 }}>{t('lockedFeatureMock.brandKit.appliesTo', 'Se aplica a los perfiles de tu equipo')}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {themes.map((th, i) => (
          <div key={i} style={{ flex: 1, background: '#fff', border: i === 0 ? '1.5px solid #0A0A0A' : '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: th.button }} />
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: th.accent }} />
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#0A0A0A' }}>{th.name}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 12, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, color: '#6B6B6B' }}>{t('lockedFeatureMock.brandKit.applyToProfiles', 'Aplicar a 4 de 4 perfiles')}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#0A0A0A', padding: '5px 12px', borderRadius: 999 }}>{t('lockedFeatureMock.brandKit.apply', 'Aplicar')}</span>
      </div>
    </div>
  );
};

export const FEATURE_MOCKS = {
  analytics: AnalyticsMock,
  teams: TeamsMock,
  team_analytics: TeamAnalyticsMock,
  hub: HubMock,
  campaigns: CampaignsMock,
  reminders: RemindersMock,
  brand_kit: BrandKitMock,
};
